import { catalogById, eraOptions } from "../catalog";
import { FIELDS, type Appearance, type DailySnapshot, type Field, type FamiliarityRecord, type GameScope, type GameSettings, type Grade, type HistoryEntry, type PersistedStateV1, type SessionLength } from "../types";
import { createDefaultState, defaultSettings } from "./defaults";

export const STORAGE_KEY = "guess-that-set:v1";

export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export interface StorageAdapter {
  readonly available: boolean;
  readonly warning: string | null;
  load(): string | null;
  save(value: string): boolean;
  reset(): boolean;
}

export function createStorageAdapter(storage: StorageLike | null | undefined): StorageAdapter {
  if (!storage) {
    return { available: false, warning: "Progress is running in memory because local storage is unavailable.", load: () => null, save: () => false, reset: () => false };
  }
  try {
    const probe = `${STORAGE_KEY}:probe`;
    storage.setItem(probe, "1");
    storage.removeItem(probe);
  } catch {
    return { available: false, warning: "Progress is running in memory because local storage is unavailable.", load: () => null, save: () => false, reset: () => false };
  }
  let warning: string | null = null;
  return {
    get available() { return warning === null; },
    get warning() { return warning; },
    load() {
      try {
        return storage.getItem(STORAGE_KEY);
      } catch {
        warning = "Progress could not be read from local storage; this session will continue in memory.";
        return null;
      }
    },
    save(value) {
      try {
        storage.setItem(STORAGE_KEY, value);
        return true;
      } catch {
        warning = "Progress could not be saved locally; this session will continue in memory.";
        return false;
      }
    },
    reset() {
      try {
        storage.removeItem(STORAGE_KEY);
        return true;
      } catch {
        warning = "Progress could not be cleared from local storage.";
        return false;
      }
    },
  };
}

function isObject(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function uniqueFields(value: unknown): Field[] {
  if (!Array.isArray(value)) return [];
  return Array.from(new Set(value.filter((field): field is Field => typeof field === "string" && FIELDS.includes(field as Field))));
}

function sanitizeLength(value: unknown): SessionLength {
  return value === 5 || value === 10 || value === 20 || value === "endless" ? value : defaultSettings.sessionLength;
}

function sanitizeAppearance(value: unknown): Appearance {
  return value === "light" || value === "dark" || value === "system" ? value : defaultSettings.appearance;
}

function validSetIds(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return Array.from(new Set(value.filter((id): id is string => typeof id === "string" && catalogById.has(id))));
}

function sanitizeScope(value: unknown): GameScope {
  if (!isObject(value) || typeof value.kind !== "string") return structuredClone(defaultSettings.scope);
  if (value.kind === "all") return { kind: "all" };
  if (value.kind === "eras") {
    const validEraIds = new Set(eraOptions.map((era) => era.id));
    const eraIds = Array.from(new Set(Array.isArray(value.eraIds) ? value.eraIds.filter((id): id is string => typeof id === "string" && validEraIds.has(id)) : []));
    return eraIds.length > 0 ? { kind: "eras", eraIds } : structuredClone(defaultSettings.scope);
  }
  if (value.kind === "custom") {
    const setIds = validSetIds(value.setIds);
    return setIds.length > 0 ? { kind: "custom", setIds } : structuredClone(defaultSettings.scope);
  }
  return structuredClone(defaultSettings.scope);
}

export function sanitizeSettings(value: unknown): GameSettings {
  const source = isObject(value) ? value : {};
  const fields = uniqueFields(source.fields);
  const safeFields = fields.length > 0 ? fields : [...defaultSettings.fields];
  const clues = uniqueFields(source.clues).filter((field) => !safeFields.includes(field));
  return {
    fields: safeFields,
    clues,
    scope: sanitizeScope(source.scope),
    sessionLength: sanitizeLength(source.sessionLength),
    appearance: sanitizeAppearance(source.appearance),
  };
}

function finiteNumber(value: unknown, fallback = 0): number {
  return typeof value === "number" && Number.isFinite(value) ? value : fallback;
}

function clampFamiliarity(value: unknown): number {
  return Math.min(1, Math.max(0, finiteNumber(value)));
}

function sanitizeFamiliarity(value: unknown): FamiliarityRecord {
  const source = isObject(value) ? value : {};
  const seen = Math.max(0, Math.floor(finiteNumber(source.seen)));
  return {
    name: clampFamiliarity(source.name),
    era: clampFamiliarity(source.era),
    year: clampFamiliarity(source.year),
    seen,
    lastSeen: typeof source.lastSeen === "string" && !Number.isNaN(Date.parse(source.lastSeen)) ? source.lastSeen : null,
  };
}

function sanitizeGrade(value: unknown): Grade | null {
  return value === "correct" || value === "close" || value === "incorrect" || value === "skip" ? value : null;
}

function sanitizeHistory(value: unknown): HistoryEntry[] {
  if (!Array.isArray(value)) return [];
  const result: HistoryEntry[] = [];
  for (const entry of value) {
    if (!isObject(entry) || typeof entry.setId !== "string" || !catalogById.has(entry.setId)) continue;
    const asked = uniqueFields(entry.asked);
    if (asked.length === 0) continue;
    const clues = uniqueFields(entry.clues).filter((field) => !asked.includes(field));
    const answers: HistoryEntry["answers"] = {};
    if (isObject(entry.answers)) {
      for (const field of asked) {
        const answer = entry.answers[field];
        if (typeof answer === "string" || (typeof answer === "number" && Number.isFinite(answer))) answers[field] = answer;
      }
    }
    const resultByField: HistoryEntry["result"] = {};
    if (isObject(entry.result)) {
      for (const field of asked) {
        const grade = sanitizeGrade(entry.result[field]);
        if (grade) resultByField[field] = grade;
      }
    }
    const familiarityAfter: HistoryEntry["familiarityAfter"] = {};
    if (isObject(entry.familiarityAfter)) {
      for (const field of asked) {
        if (typeof entry.familiarityAfter[field] === "number") familiarityAfter[field] = clampFamiliarity(entry.familiarityAfter[field]);
      }
    }
    result.push({
      id: typeof entry.id === "string" && entry.id ? entry.id : makeId(),
      setId: entry.setId,
      asked,
      clues,
      answers,
      result: resultByField,
      points: Math.max(0, Math.floor(finiteNumber(entry.points))),
      familiarityAfter,
      at: typeof entry.at === "string" && !Number.isNaN(Date.parse(entry.at)) ? entry.at : new Date(0).toISOString(),
    });
    if (result.length === 100) break;
  }
  return result;
}

function sanitizeSnapshots(value: unknown): DailySnapshot[] {
  if (!Array.isArray(value)) return [];
  const snapshots: DailySnapshot[] = [];
  for (const item of value) {
    if (!isObject(item) || typeof item.date !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(item.date)) continue;
    snapshots.push({ date: item.date, overall: clampFamiliarity(item.overall) });
  }
  const deduped = new Map(snapshots.map((item) => [item.date, item]));
  return Array.from(deduped.values()).sort((a, b) => a.date.localeCompare(b.date)).slice(-30);
}

export function migrateAndSanitize(value: unknown): PersistedStateV1 {
  if (!isObject(value) || value.version !== 1) return createDefaultState();
  const familiarity: PersistedStateV1["familiarity"] = {};
  if (isObject(value.familiarity)) {
    for (const [id, record] of Object.entries(value.familiarity)) {
      if (catalogById.has(id)) familiarity[id] = sanitizeFamiliarity(record);
    }
  }
  const totals = isObject(value.totals) ? value.totals : {};
  return {
    version: 1,
    settings: sanitizeSettings(value.settings),
    familiarity,
    history: sanitizeHistory(value.history),
    totals: {
      score: Math.max(0, Math.floor(finiteNumber(totals.score))),
      streak: Math.max(0, Math.floor(finiteNumber(totals.streak))),
    },
    dailySnapshots: sanitizeSnapshots(value.dailySnapshots),
  };
}

export function loadState(adapter: StorageAdapter): { state: PersistedStateV1; warning: string | null } {
  const serialized = adapter.load();
  if (!serialized) return { state: createDefaultState(), warning: adapter.warning };
  try {
    return { state: migrateAndSanitize(JSON.parse(serialized) as unknown), warning: adapter.warning };
  } catch {
    return { state: createDefaultState(), warning: "Saved progress was unreadable, so a fresh local game was started." };
  }
}

export function saveState(adapter: StorageAdapter, state: PersistedStateV1): string | null {
  const saved = adapter.save(JSON.stringify(state));
  return saved ? null : adapter.warning;
}

export function resetState(adapter: StorageAdapter): { state: PersistedStateV1; warning: string | null } {
  adapter.reset();
  return { state: createDefaultState(), warning: adapter.warning };
}

export function serializeState(state: PersistedStateV1): string {
  return JSON.stringify(state, null, 2);
}

export function makeId(): string {
  try {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") return crypto.randomUUID();
  } catch {
    // Fall through for older browsers and restricted contexts.
  }
  return `round-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}
